import { useLayoutEffect, useRef } from "react";
import { labelsOverOutline } from "./axisLabelCovers.lib";
import {
    WINDOW_BODY_SELECTOR,
    WINDOW_MOVING_SELECTOR,
    WINDOW_STROKE_PX,
} from "./windowOverlay.lib";

/** How long to keep measuring after the bars or window last moved, e.g. between a trackpad's wheel events. */
const STILL_MS = 150;

/** Whether a transition or animation under `root` is still running, skipping endless ones. */
const isAnimating = (root: HTMLElement) =>
    (root.getAnimations?.({ subtree: true }) ?? []).some(
        ({ effect, playState }) =>
            playState === "running" &&
            effect?.getComputedTiming().endTime !== Infinity,
    );

/** Whether the window is being dragged or easing into place after a drop. */
const isWindowMoving = (root: HTMLElement) =>
    root.querySelector(WINDOW_MOVING_SELECTOR) !== null;

function markLabelsOverOutline(root: HTMLElement) {
    const outline = root.querySelector(WINDOW_BODY_SELECTOR);
    const labels = [...root.querySelectorAll<HTMLElement>("[data-axis-label]")];
    const covers = outline
        ? labelsOverOutline({
              labels: labels.map((label) => label.getBoundingClientRect()),
              outline: outline.getBoundingClientRect(),
              stroke: WINDOW_STROKE_PX,
          })
        : [];
    labels.forEach((label, index) => {
        const next = String(covers[index] ?? false);
        if (label.dataset.covers !== next) {
            label.dataset.covers = next;
        }
    });
}

/**
 * Marks the axis labels the window's outline runs through with
 * `data-covers`, so CSS backs them and the line breaks around their text.
 * Whenever `motionKey` changes or the pointer drags the window, the labels
 * are measured every frame until the bars, the window and their animations
 * are still again, so the backing follows the outline while it moves.
 */
export function useAxisLabelCovers({
    root,
    motionKey,
}: {
    root: HTMLElement | null;
    /** Changes whenever the bars or the window's range change. */
    motionKey: string;
}) {
    const trackRef = useRef<(() => void) | null>(null);

    useLayoutEffect(() => {
        if (!root) {
            return undefined;
        }
        let movedAt = 0;
        let frame = 0;
        const measure = () => {
            markLabelsOverOutline(root);
            const isMoving =
                performance.now() - movedAt < STILL_MS ||
                isWindowMoving(root) ||
                isAnimating(root);
            frame = isMoving ? requestAnimationFrame(measure) : 0;
        };
        const track = () => {
            movedAt = performance.now();
            if (frame === 0) {
                measure();
            }
        };
        const trackDrag = () => {
            if (isWindowMoving(root)) {
                track();
            }
        };
        trackRef.current = track;
        root.addEventListener("pointermove", trackDrag);
        return () => {
            root.removeEventListener("pointermove", trackDrag);
            cancelAnimationFrame(frame);
            trackRef.current = null;
        };
    }, [root]);

    useLayoutEffect(() => {
        trackRef.current?.();
    }, [root, motionKey]);
}
